import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimelineSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const timelineContent = {
    ar: {
      title: "الجدول الزمني للحرية",
      subtitle: "رحلة النضال السوري نحو الحرية",
      description: "تتبع المحطات المهمة في تاريخ النضال السوري من أجل الحرية والكرامة",
      shareText: "شارك هذا الحدث",
      readMoreText: "اقرأ المزيد",
      readLessText: "اقرأ أقل"
    },
    en: {
      title: "Timeline of Freedom",
      subtitle: "Syrian Journey Towards Liberation",
      description: "Follow the important milestones in the history of Syrian struggle for freedom and dignity",
      shareText: "Share this event",
      readMoreText: "Read More",
      readLessText: "Read Less"
    },
    tr: {
      title: "Özgürlük Zaman Çizelgesi",
      subtitle: "Suriye\'nin Kurtuluşa Doğru Yolculuğu",
      description: "Suriye\'nin özgürlük ve onur mücadelesinin tarihindeki önemli dönüm noktalarını takip edin",
      shareText: "Bu olayı paylaş",
      readMoreText: "Devamını Oku",
      readLessText: "Daha Az Oku"
    }
  };

  const timelineEvents = [
    {
      id: 1,
      date: "2011-03-15",
      dateAr: "١٥ مارس ٢٠١١",
      dateTr: "15 Mart 2011",
      title: "Beginning of the Syrian Revolution",
      titleAr: "بداية الثورة السورية",
      titleTr: "Suriye Devriminin Başlangıcı",
      summary: "Peaceful protests begin in Daraa",
      summaryAr: "بداية الاحتجاجات السلمية في درعا",
      summaryTr: "Dera\'da barışçıl protestolar başlıyor",
      description: `The Syrian revolution began with peaceful protests in the southern city of Daraa after local children were arrested and tortured for writing anti-government graffiti. What started as calls for the release of political prisoners quickly evolved into nationwide demonstrations demanding freedom, dignity, and democratic reforms.

The initial protests were met with violent crackdowns by security forces, but this only fueled more demonstrations across the country. Citizens from all walks of life joined the movement, united by their desire for change and their rejection of decades of authoritarian rule.`,
      descriptionAr: `بدأت الثورة السورية بمظاهرات سلمية في مدينة درعا الجنوبية بعد اعتقال وتعذيب أطفال محليين لكتابتهم شعارات مناهضة للحكومة. ما بدأ كدعوات لإطلاق سراح المعتقلين السياسيين تطور بسرعة إلى مظاهرات على مستوى البلاد تطالب بالحرية والكرامة والإصلاحات الديمقراطية.

قوبلت الاحتجاجات الأولى بقمع عنيف من قبل قوات الأمن، لكن هذا لم يؤد إلا إلى المزيد من المظاهرات في جميع أنحاء البلاد. انضم مواطنون من جميع مناحي الحياة إلى الحركة، متحدين برغبتهم في التغيير ورفضهم لعقود من الحكم الاستبدادي.`,
      descriptionTr: `Suriye devrimi, yerel çocukların hükümete karşı grafiti yazdıkları için tutuklanıp işkence görmesinin ardından güney şehri Dera'da barışçıl protestolarla başladı. Siyasi mahkumların serbest bırakılması çağrılarıyla başlayan hareket, hızla özgürlük, onur ve demokratik reformlar talep eden ülke çapında gösterilere dönüştü.

İlk protestolar güvenlik güçleri tarafından şiddetli bastırmayla karşılandı, ancak bu sadece ülke genelinde daha fazla gösteriye yol açtı. Hayatın her kesiminden vatandaşlar, değişim arzuları ve onlarca yıllık otoriter yönetimi reddetmeleriyle birleşerek harekete katıldı.`,
      icon: "Flag",
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      id: 2,
      date: "2013-08-21",
      dateAr: "٢١ أغسطس ٢٠١٣",
      dateTr: "21 Ağustos 2013",
      title: "Chemical Weapons Attack in Ghouta",
      titleAr: "هجوم الأسلحة الكيميائية في الغوطة",
      titleTr: "Guta\'da Kimyasal Silah Saldırısı",
      summary: "Tragic escalation of violence against civilians",
      summaryAr: "تصعيد مأساوي للعنف ضد المدنيين",
      summaryTr: "Sivillere karşı şiddetin trajik tırmanışı",
      description: `One of the darkest days in Syrian history occurred when chemical weapons were used against civilians in the Ghouta region near Damascus. This attack marked a horrific escalation in the conflict and demonstrated the regime's willingness to use any means to suppress the uprising. The international community's response to this atrocity became a turning point in global awareness of the Syrian crisis. Despite widespread condemnation, the lack of decisive international action emboldened the regime to continue its brutal campaign against its own people.

This event highlighted the urgent need for international protection of civilians and accountability for war crimes, themes that would continue to resonate throughout the conflict.`,
      descriptionAr: `حدث أحد أحلك الأيام في التاريخ السوري عندما استُخدمت الأسلحة الكيميائية ضد المدنيين في منطقة الغوطة قرب دمشق. شكل هذا الهجوم تصعيداً مروعاً في الصراع وأظهر استعداد النظام لاستخدام أي وسيلة لقمع الانتفاضة.

أصبح رد المجتمع الدولي على هذه الفظاعة نقطة تحول في الوعي العالمي بالأزمة السورية. رغم الإدانة الواسعة، فإن عدم وجود عمل دولي حاسم شجع النظام على مواصلة حملته الوحشية ضد شعبه.

سلط هذا الحدث الضوء على الحاجة الملحة للحماية الدولية للمدنيين والمساءلة عن جرائم الحرب، وهي موضوعات استمرت في الصدى طوال الصراع.`,
      descriptionTr: `Suriye tarihinin en karanlık günlerinden biri, Şam yakınlarındaki Guta bölgesinde sivillere karşı kimyasal silah kullanıldığında yaşandı. Bu saldırı çatışmada korkunç bir tırmanışı işaret etti ve rejimin ayaklanmayı bastırmak için her türlü aracı kullanma istekliliğini gösterdi.

Uluslararası toplumun bu vahşete verdiği tepki, Suriye krizine yönelik küresel farkındalıkta bir dönüm noktası oldu. Yaygın kınamaya rağmen, kararlı uluslararası eylemin olmaması rejimi kendi halkına karşı acımasız kampanyasını sürdürmeye cesaretlendirdi.

Bu olay, sivillerin uluslararası korunması ve savaş suçları için hesap verebilirliğin acil ihtiyacını vurguladı; bu temalar çatışma boyunca yankılanmaya devam edecekti.`,
      icon: "AlertTriangle",
      color: "text-error",
      bgColor: "bg-error/10"
    },
    {
      id: 3,
      date: "2016-12-22",
      dateAr: "٢٢ ديسمبر ٢٠١٦",
      dateTr: "22 Aralık 2016",
      title: "Fall of Aleppo",
      titleAr: "سقوط حلب",
      titleTr: "Halep\'in Düşüşü",
      summary: "End of opposition control in Syria\'s largest city",
      summaryAr: "نهاية سيطرة المعارضة في أكبر مدن سوريا",
      summaryTr: "Suriye\'nin en büyük şehrinde muhalefet kontrolünün sonu",
      description: `The fall of Aleppo marked a significant turning point in the Syrian conflict. After years of siege and bombardment, the last opposition-held areas of Syria's largest city fell to government forces. This event symbolized the immense human cost of the conflict and the resilience of those who fought for freedom.

The siege of Aleppo became a symbol of the international community's failure to protect civilians. Despite numerous ceasefire agreements and humanitarian appeals, the bombardment continued, forcing thousands of families to flee their homes and seek refuge elsewhere.

The fall of Aleppo did not end the Syrian people's desire for freedom and dignity. Instead, it strengthened their resolve to continue the struggle through different means, including civil society organizations, cultural preservation, and international advocacy.`,
      descriptionAr: `شكل سقوط حلب نقطة تحول مهمة في الصراع السوري. بعد سنوات من الحصار والقصف، سقطت آخر المناطق التي تسيطر عليها المعارضة في أكبر مدن سوريا في يد القوات الحكومية. رمز هذا الحدث إلى التكلفة البشرية الهائلة للصراع وصمود أولئك الذين قاتلوا من أجل الحرية.

أصبح حصار حلب رمزاً لفشل المجتمع الدولي في حماية المدنيين. رغم اتفاقيات وقف إطلاق النار العديدة والنداءات الإنسانية، استمر القصف، مما أجبر آلاف العائلات على الفرار من منازلهم والبحث عن ملجأ في مكان آخر.

لم ينه سقوط حلب رغبة الشعب السوري في الحرية والكرامة. بدلاً من ذلك، عزز عزيمتهم على مواصلة النضال بوسائل مختلفة، بما في ذلك منظمات المجتمع المدني والحفاظ على الثقافة والدعوة الدولية.`,
      descriptionTr: `Halep'in düşüşü Suriye çatışmasında önemli bir dönüm noktasını işaret etti. Yıllarca süren kuşatma ve bombardımandan sonra, Suriye'nin en büyük şehrinin son muhalefet kontrolündeki bölgeleri hükümet güçlerinin eline geçti. Bu olay çatışmanın muazzam insani maliyetini ve özgürlük için savaşanların direncini simgeledi.

Halep kuşatması uluslararası toplumun sivilleri korumadaki başarısızlığının sembolü haline geldi. Sayısız ateşkes anlaşması ve insani yardım çağrısına rağmen bombardıman devam etti, binlerce aileyi evlerini terk etmeye ve başka yerlerde sığınak aramaya zorladı.

Halep'in düşüşü Suriye halkının özgürlük ve onur arzusunu sona erdirmedi. Bunun yerine, sivil toplum kuruluşları, kültürel koruma ve uluslararası savunuculuk dahil olmak üzere farklı yollarla mücadeleyi sürdürme kararlılıklarını güçlendirdi.`,
      icon: "Home",
      color: "text-warning",
      bgColor: "bg-warning/10"
    },
    {
      id: 4,
      date: "2024-12-08",
      dateAr: "٨ ديسمبر ٢٠٢٤",
      dateTr: "8 Aralık 2024",
      title: "Liberation Day - Fall of Assad Regime",
      titleAr: "يوم التحرير - سقوط نظام الأسد",
      titleTr: "Kurtuluş Günü - Esad Rejiminin Düşüşü",
      summary: "The end of 54 years of Assad family rule",
      summaryAr: "نهاية ٥٤ عاماً من حكم عائلة الأسد",
      summaryTr: "Esad ailesinin 54 yıllık yönetiminin sonu",
      description: `December 8, 2024, will forever be remembered as Liberation Day - the day when the Syrian people finally achieved their long-awaited freedom. After more than five decades of Assad family rule, the regime that had oppressed the Syrian people for generations finally fell.

This historic moment represents the culmination of years of struggle, sacrifice, and unwavering determination by the Syrian people. Despite facing unimaginable hardships, displacement, and loss, they never gave up on their dream of freedom and dignity.

The fall of the Assad regime opens a new chapter in Syrian history - one filled with hope, possibility, and the promise of rebuilding a nation based on justice, equality, and human rights. This memorial stands as a testament to all those who sacrificed for this moment and as a beacon of hope for future generations.`,
      descriptionAr: `سيُذكر الثامن من ديسمبر ٢٠٢٤ إلى الأبد كيوم التحرير - اليوم الذي حقق فيه الشعب السوري أخيراً حريته المنتظرة طويلاً. بعد أكثر من خمسة عقود من حكم عائلة الأسد، سقط أخيراً النظام الذي قهر الشعب السوري لأجيال.

تمثل هذه اللحظة التاريخية ذروة سنوات من النضال والتضحية والعزيمة الثابتة من قبل الشعب السوري. رغم مواجهة مصاعب لا يمكن تصورها والنزوح والخسارة، لم يتخلوا أبداً عن حلمهم في الحرية والكرامة.

يفتح سقوط نظام الأسد فصلاً جديداً في التاريخ السوري - فصل مليء بالأمل والإمكانيات ووعد إعادة بناء أمة قائمة على العدالة والمساواة وحقوق الإنسان. يقف هذا النصب التذكاري شاهداً على كل من ضحى من أجل هذه اللحظة ومنارة أمل للأجيال القادمة.`,
      descriptionTr: `8 Aralık 2024, sonsuza kadar Kurtuluş Günü olarak hatırlanacak - Suriye halkının nihayet uzun zamandır beklenen özgürlüğünü elde ettiği gün. Esad ailesinin yarım asırdan fazla süren yönetiminden sonra, nesillerce Suriye halkını ezen rejim nihayet düştü.

Bu tarihi an, Suriye halkının yıllarca süren mücadele, fedakarlık ve sarsılmaz kararlılığının doruk noktasını temsil ediyor. Hayal edilemez zorluklarla, yerinden edilme ve kayıplarla karşılaştıkları halde, özgürlük ve onur hayallerinden asla vazgeçmediler.

Esad rejiminin düşüşü Suriye tarihinde yeni bir sayfa açıyor - umut, olasılık ve adalet, eşitlik ve insan haklarına dayalı bir ulusu yeniden inşa etme vaadi ile dolu bir sayfa. Bu anıt, bu an için fedakarlık yapan herkese tanıklık ediyor ve gelecek nesiller için bir umut feneri olarak duruyor.`,
      icon: "Sun",
      color: "text-success",
      bgColor: "bg-success/10"
    }
  ];

  const getDate = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.dateAr;
      case 'tr': return event?.dateTr;
      default: return new Date(event.date)?.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  const getTitle = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.titleAr;
      case 'tr': return event?.titleTr;
      default: return event?.title;
    }
  };

  const getSummary = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.summaryAr;
      case 'tr': return event?.summaryTr;
      default: return event?.summary;
    }
  };

  const getDescription = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.descriptionAr;
      case 'tr': return event?.descriptionTr;
      default: return event?.description;
    }
  };

  const toggleExpanded = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  const shareEvent = (event) => {
    if (navigator.share) {
      navigator.share({
        title: getTitle(event),
        text: getSummary(event),
        url: window.location?.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const text = `${getTitle(event)} - ${getSummary(event)} ${window.location?.href}`;
      navigator.clipboard?.writeText(text);
    }
  };

  const content = timelineContent?.[currentLanguage];

  return (
    <section id="timeline" className="py-16 lg:py-24 bg-[#006e4c]/3">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {content?.title}
          </h2>
          <p className="font-body text-xl text-accent mb-6">
            {content?.subtitle}
          </p>
          <p className="font-body text-base md:text-lg text-text-secondary max-w-3xl mx-auto">
            {content?.description}
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

          <div className="space-y-8">
            {timelineEvents?.map((event, index) => (
              <motion.div
                key={event?.id}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="relative"
              >
                {/* Timeline Dot */}
                <div className={`absolute left-6 w-4 h-4 ${event?.bgColor} border-2 border-background rounded-full flex items-center justify-center`}>
                  <div className={`w-2 h-2 ${event?.color?.replace('text-', 'bg-')} rounded-full`}></div>
                </div>

                {/* Event Card */}
                <div className="ml-16 bg-card border border-border rounded-lg shadow-memorial overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 ${event?.bgColor} rounded-lg flex items-center justify-center`}>
                          <Icon name={event?.icon} size={20} className={event?.color} />
                        </div>
                        <div>
                          <p className="font-body text-sm text-text-secondary">
                            {getDate(event)}
                          </p>
                          <h3 className="font-body text-lg md:text-xl font-semibold text-primary">
                            {getTitle(event)}
                          </h3>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => shareEvent(event)}
                        iconName="Share2"
                        iconPosition="left"
                        className="flex-shrink-0 share-button"
                      >
                        {content?.shareText}
                      </Button>
                    </div>

                    <p className="font-body text-text-secondary mb-4">
                      {getSummary(event)}
                    </p>

                    {expandedEvent === event?.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-border pt-4 mb-4"
                      >
                        <p className="font-body text-text-primary leading-relaxed whitespace-pre-line">
                          {getDescription(event)}
                        </p>
                      </motion.div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleExpanded(event?.id)}
                      iconName={expandedEvent === event?.id ? 'ChevronUp' : 'ChevronDown'}
                      iconPosition="right"
                    >
                      {expandedEvent === event?.id ? content?.readLessText : content?.readMoreText}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Memorial Date Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border border-primary/20 rounded-lg p-6 md:p-8">
            <div className="flex items-center justify-center mb-4">
              <Icon name="Calendar" size={32} className="text-primary" />
            </div>
            <h3 className="font-memorial text-2xl md:text-3xl font-bold text-primary mb-2">
              {currentLanguage === 'ar' ? 'يوم التحرير' : 
               currentLanguage === 'tr' ? 'Kurtuluş Günü' : 'Liberation Day'}
            </h3>
            <p className="font-body text-lg text-accent mb-4">
              {currentLanguage === 'ar' ? '٨ ديسمبر ٢٠٢٤' : 
               currentLanguage === 'tr' ? '8 Aralık 2024' : 'December 8, 2024'}
            </p>
            <p className="font-body text-text-secondary max-w-2xl mx-auto">
              {currentLanguage === 'ar' ?'اليوم الذي تحققت فيه أحلام الشعب السوري في الحرية والكرامة'
                : currentLanguage === 'tr' ?'Suriye halkınınözgürlük ve onur hayallerinin gerçekleştiği gün' :'The day when Syrian people\'s dreams of freedom and dignity came true'}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TimelineSection;